/**
 * Field PWA route definitions.
 *
 * All field pages are lazy-loaded so /field/* stays out of the desktop bundle.
 * The leaf components are imported once here; routes.tsx just spreads the
 * exported array into its top-level routes list.
 *
 * Layout vs. content split:
 *   - /field/login uses no layout (full-bleed login screen).
 *   - Everything else nests under <FieldLayout> which provides the header,
 *     bottom tab bar, auth guard, and safe-area handling.
 *   - Each authenticated route is wrapped in <RequirePermission> with its
 *     pageKey so role-based gating is a one-line change away.
 *
 * Suspense:
 *   Every lazy element is wrapped in <Suspense> at the route level via the
 *   `element()` helper below. Without this, navigating from outside the
 *   field tree (or to the layout itself for the first time) suspends above
 *   the route and React throws the "synchronous input" error. The pattern
 *   mirrors what the desktop tree's wrapper components do.
 */

import { Loader2 } from 'lucide-react'
import { Suspense, lazy, type ReactNode } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'

import { RequireAnyPermission } from './permissions/RequireAnyPermission.js'
import { RequirePermission } from './permissions/RequirePermission.js'
import {
  FIELD_PAGE_KEYS,
  PRODUCTION_HUB_PAGE_KEYS,
  WAREHOUSE_HUB_PAGE_KEYS,
  type FieldPageKey,
} from './permissions/usePagePermission.js'

// Lazy leaves — keeps the desktop bundle clean.
const FieldLayout = lazy(() => import('./layout/FieldLayout.js').then((m) => ({ default: m.FieldLayout })))
const FieldLoginPage = lazy(() => import('./pages/FieldLoginPage.js').then((m) => ({ default: m.FieldLoginPage })))
const InstallPage = lazy(() => import('./pages/InstallPage.js').then((m) => ({ default: m.InstallPage })))
const HomePicker = lazy(() => import('./pages/HomePicker.js').then((m) => ({ default: m.HomePicker })))
const SettingsPage = lazy(() => import('./pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage })))
const SyncPage = lazy(() => import('./pages/SyncPage.js').then((m) => ({ default: m.SyncPage })))
// Help: the office components with mount="field" (docs/help/HELP_SECTION_PLAN.md §4).
const HelpIndex = lazy(() => import('../help/HelpIndex.js'))
const HelpGuide = lazy(() => import('../help/HelpGuide.js'))

// Hub pages — the bottom-bar entry screens that nest the leaf flows below.
const WarehouseHome = lazy(() => import('./warehouse/WarehouseHome.js').then((m) => ({ default: m.WarehouseHome })))
const ProductionHome = lazy(() => import('./production/ProductionHome.js').then((m) => ({ default: m.ProductionHome })))

// Slice A: Warehouse pages.
const ScanPage = lazy(() => import('./warehouse/ScanPage.js').then((m) => ({ default: m.ScanPage })))
const InventorySearchPage = lazy(() => import('./warehouse/InventorySearchPage.js').then((m) => ({ default: m.InventorySearchPage })))
const InventoryDetailPage = lazy(() => import('./warehouse/InventoryDetailPage.js').then((m) => ({ default: m.InventoryDetailPage })))
const ReceivePage = lazy(() => import('./warehouse/ReceivePage.js').then((m) => ({ default: m.ReceivePage })))
const CycleCountPage = lazy(() => import('./warehouse/cycle-count/CycleCountPage.js').then((m) => ({ default: m.CycleCountPage })))
const WarehousePinPad = lazy(() => import('./warehouse/WarehousePinPad.js').then((m) => ({ default: m.WarehousePinPad })))
const WarehouseMyWorkPage = lazy(() => import('./warehouse/WarehouseMyWorkPage.js').then((m) => ({ default: m.WarehouseMyWorkPage })))

// Slice B: Jobsite pages.
const LookupPage = lazy(() => import('./jobsite/LookupPage.js').then((m) => ({ default: m.LookupPage })))
const JobOverviewPage = lazy(() => import('./jobsite/JobOverviewPage.js').then((m) => ({ default: m.JobOverviewPage })))
const LotDetailPage = lazy(() => import('./jobsite/LotDetailPage.js').then((m) => ({ default: m.LotDetailPage })))
const AreaDetailPage = lazy(() => import('./jobsite/AreaDetailPage.js').then((m) => ({ default: m.AreaDetailPage })))
const PhotosNewPage = lazy(() => import('./jobsite/PhotosNewPage.js').then((m) => ({ default: m.PhotosNewPage })))

// Slice C: Production department (card aaHMG95zsq83).
const ProductionPage = lazy(() => import('./production/ProductionPage.js').then((m) => ({ default: m.ProductionPage })))
// Slice D: Field install (installer completion capture + report).
const InstallerJobsPage = lazy(() => import('./install/InstallerJobsPage.js').then((m) => ({ default: m.InstallerJobsPage })))
const InstallActivityPage = lazy(() => import('./install/InstallActivityPage.js').then((m) => ({ default: m.InstallActivityPage })))
const InstallAssignSheetPage = lazy(() => import('./install/InstallAssignSheetPage.js').then((m) => ({ default: m.InstallAssignSheetPage })))
const InstallReportPage = lazy(() => import('./install/InstallReportPage.js').then((m) => ({ default: m.InstallReportPage })))
const LotCompletionPage = lazy(() => import('./install/LotCompletionPage.js').then((m) => ({ default: m.LotCompletionPage })))

// RequirePermission is composed inline. It must be a regular import so it can
// wrap children synchronously inside a route element. It's tiny — no bundle
// concern.

/** Inline fallback for route-level Suspense boundaries. */
function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] w-full items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading…
    </div>
  )
}

/**
 * Wrap a lazy element in <Suspense> so a sync nav doesn't throw the
 * "component suspended" error. Used for every field route element.
 */
function withSuspense(node: ReactNode): ReactNode {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>
}

/** Standard "authenticated + permitted + suspense" wrapper for field pages. */
function gated(pageKey: FieldPageKey, node: ReactNode): ReactNode {
  return withSuspense(<RequirePermission pageKey={pageKey}>{node}</RequirePermission>)
}

/**
 * Hub wrapper: "authenticated + permitted for ANY of these child pages +
 * suspense." Used by the Warehouse / Production hub routes so a user who can
 * see at least one nested action can open the hub; the hub's buttons then
 * self-gate per child pageKey.
 */
function gatedAny(pageKeys: readonly FieldPageKey[], node: ReactNode): ReactNode {
  return withSuspense(<RequireAnyPermission pageKeys={pageKeys}>{node}</RequireAnyPermission>)
}

export const fieldRoutes: RouteObject[] = [
  {
    path: '/field/login',
    element: withSuspense(<FieldLoginPage />),
    handle: { title: 'Sign in' },
  },
  {
    // Public — no login required. Lives under /field on purpose: the page
    // swaps the linked manifest to /manifest.field.json (start_url:/field) via
    // useFieldManifest(), so Add to Home Screen / beforeinstallprompt triggered
    // from here installs a field PWA that LAUNCHES into the field app.
    path: '/field/get-app',
    element: withSuspense(<InstallPage />),
    handle: { title: 'Install Lithos Field' },
  },
  {
    path: '/field',
    element: withSuspense(<FieldLayout />),
    children: [
      { index: true,           element: gated(FIELD_PAGE_KEYS.HOME, <HomePicker />),       handle: { title: 'Home' } },
      { path: 'home',          element: gated(FIELD_PAGE_KEYS.HOME, <HomePicker />),       handle: { title: 'Home' } },
      { path: 'settings',      element: gated(FIELD_PAGE_KEYS.SETTINGS, <SettingsPage />), handle: { title: 'Settings' } },
      { path: 'sync',          element: gated(FIELD_PAGE_KEYS.SYNC, <SyncPage />),         handle: { title: 'Sync' } },
      { path: 'help',          element: gated(FIELD_PAGE_KEYS.HELP, <HelpIndex mount="field" />), handle: { title: 'Help' } },
      { path: 'help/:slug',    element: gated(FIELD_PAGE_KEYS.HELP, <HelpGuide mount="field" />), handle: { title: 'Help' } },

      // ── Hub: Warehouse ── (bottom-tab entry that nests the warehouse +
      // lookup leaf flows as buttons; each button self-gates per child perm).
      { path: 'warehouse',     element: gatedAny(WAREHOUSE_HUB_PAGE_KEYS, <WarehouseHome />),                     handle: { title: 'Warehouse' } },
      // PIN pad: a production worker taps in on the shared tablet to claim their
      // assigned work. Gated like the warehouse hub (any warehouse-capable user
      // reaching the hub can open it); no dedicated feature perm is seeded.
      { path: 'warehouse/clock-in', element: gatedAny(WAREHOUSE_HUB_PAGE_KEYS, <WarehousePinPad />),             handle: { title: 'Enter PIN' } },
      // WH-2: claim-based lot-serial search — the warehouse worker's landing after
      // PIN. Replaces the assignment-driven /field/my-activities entry for
      // warehouse. Gated like the hub (any warehouse-capable user).
      { path: 'warehouse/my-work', element: gatedAny(WAREHOUSE_HUB_PAGE_KEYS, <WarehouseMyWorkPage />),          handle: { title: 'My production work' } },

      // ── Slice A: Warehouse ──
      { path: 'scan',          element: gated(FIELD_PAGE_KEYS.WAREHOUSE_SCAN, <ScanPage />),                       handle: { title: 'Scan' } },
      { path: 'inventory',     element: gated(FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY, <InventorySearchPage />),       handle: { title: 'Inventory' } },
      { path: 'inventory/:id', element: gated(FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY_DETAIL, <InventoryDetailPage />),handle: { title: 'Inventory item' } },
      { path: 'receive',       element: gated(FIELD_PAGE_KEYS.WAREHOUSE_RECEIVE, <ReceivePage />),                 handle: { title: 'Receive' } },
      { path: 'cycle-count',   element: gated(FIELD_PAGE_KEYS.WAREHOUSE_CYCLE_COUNT, <CycleCountPage />),          handle: { title: 'Cycle count' } },

      // ── Slice B: Jobsite ──
      { path: 'lookup',        element: gated(FIELD_PAGE_KEYS.JOBSITE_LOOKUP, <LookupPage />),       handle: { title: 'Lookup' } },
      { path: 'jobs/:id',      element: gated(FIELD_PAGE_KEYS.JOBSITE_JOB,    <JobOverviewPage />),  handle: { title: 'Job' } },
      { path: 'lots/:id',      element: gated(FIELD_PAGE_KEYS.JOBSITE_LOT,    <LotDetailPage />),    handle: { title: 'Lot' } },
      { path: 'areas/:id',     element: gated(FIELD_PAGE_KEYS.JOBSITE_AREA,   <AreaDetailPage />),   handle: { title: 'Area' } },
      { path: 'photos/new',    element: gated(FIELD_PAGE_KEYS.JOBSITE_PHOTOS, <PhotosNewPage />),    handle: { title: 'Add photos' } },

      // ── Hub: Production ── (bottom-tab entry that nests the production queue
      // + installs as buttons; each button self-gates per child perm).
      { path: 'work',          element: gatedAny(PRODUCTION_HUB_PAGE_KEYS, <ProductionHome />), handle: { title: 'Production' } },

      // ── Slice C: Production department ──
      { path: 'production',    element: gated(FIELD_PAGE_KEYS.PRODUCTION,     <ProductionPage />),   handle: { title: 'Production' } },
      // ── Universal "My Activities" surface (card XGiACzr7ZlcP) ──
      // Role-agnostic assignment view — every field user's assigned activities +
      // their read-only lot/area selections. Ungated (MY_ACTIVITIES is always-
      // allowed). This is the field HOME landing (see HomePicker).
      { path: 'my-activities',           element: gated(FIELD_PAGE_KEYS.MY_ACTIVITIES,    <InstallerJobsPage />),    handle: { title: 'My Activities' } },

      // ── Slice D: Field install ──
      // The bare '/field/install' now redirects to the universal surface — "My
      // installs" was retired in favor of "My Activities" (same component). The
      // install *capture* sub-flows (assign/report/activity/lot) remain.
      { path: 'install',                 element: <Navigate to="/field/my-activities" replace /> },
      { path: 'install/assign',          element: gated(FIELD_PAGE_KEYS.INSTALL_JOBS,     <InstallAssignSheetPage />),handle: { title: 'Assignment sheet' } },
      { path: 'install/report',          element: gated(FIELD_PAGE_KEYS.INSTALL_REPORT,   <InstallReportPage />),    handle: { title: 'Install report' } },
      // Title is the neutral "Activity" (not "Complete install"): this same page
      // renders EVERY assigned activity type — template, install, production —
      // so a hardcoded "Complete install" header misread as an install to
      // templaters. The activity's own name shows in the page body header.
      { path: 'install/activity/:id',    element: gated(FIELD_PAGE_KEYS.INSTALL_ACTIVITY, <InstallActivityPage />),  handle: { title: 'Activity' } },
      { path: 'install/lot/:lotId',      element: gated(FIELD_PAGE_KEYS.INSTALL_LOT,      <LotCompletionPage />),    handle: { title: 'Lot completion' } },
    ],
  },
]
